import { useState } from "react";
import { AlertTriangle, Brush, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import api, { repaintAndWait } from "../lib/api";
import { PaintWarningsList, paintFacts, useAdminPaintWarnings } from "./PaintWarnings";

// "Painting needs attention": the color check found a mismatch with the render. It is a WARNING only — every download
// (.3MF, .GLB, .STL) stays open; ordering a print waits until a repaint passes or the maker taps "Colors look fine".
// Everyone reads the plain reason; admins / owners also get the facts line and the per-color detail (admin route).
export const PaintAttentionCard = ({ creation, onUpdate }) => {
  const [busy, setBusy] = useState("");
  const health = creation?.paint_health;
  const fidelity = creation?.paint_fidelity;
  const healthBad = !!health && health.ok === false;
  const fidelityBad = !healthBad && !!fidelity && fidelity.ok === false && !!fidelity.reason;
  const warnings = useAdminPaintWarnings(creation, healthBad || fidelityBad);
  if (!healthBad && !fidelityBad) return null;
  const accepted = !!creation.paint_override;
  const reason = healthBad ? (health.reason || "Some colors may not print exactly as in your approved picture.") : fidelity.reason;
  const facts = paintFacts(warnings);

  const retry = async () => {
    setBusy("repaint");
    try {
      const data = await repaintAndWait(creation.id); // async repaint: starts it, then polls to completion
      onUpdate?.(data);
      const stillFlagged = data?.paint_health?.ok === false || data?.paint_fidelity?.ok === false;
      toast.success(stillFlagged ? "Repainted, but the colours still need a look." : "Colours repainted.");
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Could not repaint this duck.");
    } finally {
      setBusy("");
    }
  };

  const markFine = async () => {
    setBusy("override");
    try {
      const { data } = await api.post(`/creations/${creation.id}/paint-override`, { on: !accepted });
      onUpdate?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change that.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="paint-attention-card" className="rounded-2xl border border-amber-500/60 bg-amber-500/10 px-4 py-3">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />
        <span data-testid="paint-attention-title" className="font-bold text-sm">
          Painting needs attention
        </span>
        {facts && (
          <span data-testid="paint-attention-facts" className="ml-auto text-xs font-mono2 text-muted-foreground">
            {facts}
          </span>
        )}
      </div>
      <p data-testid="paint-attention-reason" className="text-xs text-muted-foreground mt-1.5">
        {reason}
      </p>
      <PaintWarningsList warnings={warnings} testId="paint-attention-detail" />
      <p data-testid="paint-attention-downloads" className="text-xs text-muted-foreground mt-1">
        Every download (.3MF, .GLB and .STL) stays available.{" "}
        {accepted
          ? "You marked the colors as fine, so this duck can be ordered."
          : "Ordering a print waits until a repaint passes or you tap “Colors look fine”."}
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        <Button data-testid="paint-retry-btn" onClick={retry} disabled={!!busy} className="rounded-full font-bold gap-2 h-10">
          {busy === "repaint" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Brush className="h-4 w-4" />} Repaint
        </Button>
        <Button
          data-testid="paint-override-btn"
          variant={accepted ? "secondary" : "outline"}
          onClick={markFine}
          disabled={!!busy}
          className="rounded-full font-bold gap-2 h-10"
        >
          {busy === "override" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
          {accepted ? "Marked as fine (tap to undo)" : "Colors look fine"}
        </Button>
      </div>
    </div>
  );
};
